/**
 * Client-side photo preparation: downscale and re-encode before upload.
 *
 * Memory is the constraint, not speed. A phone photo is 12 to 48 megapixels,
 * which is 50 to 200 MB once decoded, and iOS kills a home-screen web app at
 * far less than that. So the full-size image is never materialised here:
 *
 * - Where createImageBitmap honours resizeWidth/resizeHeight (Chrome, Firefox)
 *   the decoder scales while decoding and only the target-size bitmap exists.
 * - On iOS the photo is drawn from an <img> element instead. WebKit subsamples
 *   large JPEGs when drawing them to a canvas, so that path stays within budget,
 *   whereas its createImageBitmap decodes the whole thing.
 *
 * Only one target-size canvas is ever allocated, and every handle is released
 * as soon as the JPEG is encoded.
 */

const MAX_EDGE = 1600;
const QUALITY = 0.82;
const THUMB_EDGE = 320;
const THUMB_QUALITY = 0.8;

const isIOS = (): boolean =>
  /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

function fit(width: number, height: number, maxEdge: number): { w: number; h: number } {
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return { w: Math.max(1, Math.round(width * scale)), h: Math.max(1, Math.round(height * scale)) };
}

/** Loads the file into an <img>. Reading naturalWidth/Height after load does not force a full decode. */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not read photo"));
    img.src = url;
  });
}

/** Decode-at-size via createImageBitmap, or null if this browser can't or won't resize while decoding. */
async function decodeAtSize(file: File, w: number, h: number): Promise<ImageBitmap | null> {
  if (typeof createImageBitmap !== "function" || isIOS()) return null;
  try {
    const bitmap = await createImageBitmap(file, { resizeWidth: w, resizeHeight: h, resizeQuality: "high", imageOrientation: "from-image" });
    if (bitmap.width === w && bitmap.height === h) return bitmap;
    bitmap.close(); // resize ignored: a full-size bitmap we don't want to keep
  } catch {
    /* fall through to the <img> path */
  }
  return null;
}

function encode(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not encode photo"))), "image/jpeg", quality);
  });
}

/**
 * Downscales `file` to a 1600px-edge JPEG and returns it alongside a 320px
 * thumb drawn from that same canvas (never from the source file again, so
 * the full-size camera frame is decoded at most once, here).
 */
export async function prepareImage(file: File): Promise<{ full: Blob; thumb: Blob; path: "bitmap" | "img" }> {
  const url = URL.createObjectURL(file);
  let img: HTMLImageElement | null = null;
  let bitmap: ImageBitmap | null = null;
  const canvas = document.createElement("canvas");
  const thumbCanvas = document.createElement("canvas");
  try {
    img = await loadImage(url);
    // Modern browsers report EXIF-oriented dimensions here and orient on draw.
    const { w, h } = fit(img.naturalWidth, img.naturalHeight, MAX_EDGE);
    bitmap = await decodeAtSize(file, w, h);
    const path: "bitmap" | "img" = bitmap ? "bitmap" : "img";

    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable");
    ctx.drawImage(bitmap ?? img, 0, 0, w, h);
    bitmap?.close();
    bitmap = null;
    img.src = "";
    img = null;

    const { w: tw, h: th } = fit(w, h, THUMB_EDGE);
    thumbCanvas.width = tw;
    thumbCanvas.height = th;
    const tctx = thumbCanvas.getContext("2d");
    if (!tctx) throw new Error("Canvas unavailable");
    tctx.drawImage(canvas, 0, 0, tw, th);

    const [full, thumb] = await Promise.all([encode(canvas, QUALITY), encode(thumbCanvas, THUMB_QUALITY)]);
    return { full, thumb, path };
  } finally {
    bitmap?.close();
    if (img) img.src = "";
    canvas.width = 0;
    canvas.height = 0;
    thumbCanvas.width = 0;
    thumbCanvas.height = 0;
    URL.revokeObjectURL(url);
  }
}

/** Produces the same 320px thumb as `prepareImage`, from an already-uploaded 1600px JPEG. Used for backfill-style re-derivation. */
export async function makeThumb(blob: Blob): Promise<Blob> {
  const url = URL.createObjectURL(blob);
  let img: HTMLImageElement | null = null;
  const canvas = document.createElement("canvas");
  try {
    img = await loadImage(url);
    const { w, h } = fit(img.naturalWidth, img.naturalHeight, THUMB_EDGE);
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable");
    ctx.drawImage(img, 0, 0, w, h);
    img.src = "";
    img = null;
    return await encode(canvas, THUMB_QUALITY);
  } finally {
    if (img) img.src = "";
    canvas.width = 0;
    canvas.height = 0;
    URL.revokeObjectURL(url);
  }
}
